import { isNil } from 'lodash-es';
import { useCallback } from 'react';

import { NUMBER_SPIN_BUTTON_DEFAULTS } from '../constants.js';
import { NumberSpinButtonDisplayFormat } from '../types.js';

const reParseValue = new RegExp('(-?\\d+)$');
/**
 * This hook provides helper functions to parse, validate and format input values.
 */
export const useValidationAndFormatting = (
  displayFormat?: NumberSpinButtonDisplayFormat,
  required?: boolean,
  minValue: number = NUMBER_SPIN_BUTTON_DEFAULTS.MIN_VALUE,
  maxValue: number = NUMBER_SPIN_BUTTON_DEFAULTS.MAX_VALUE,
) => {
  const formatLength = displayFormat?.length ?? 0;
  const formatChars =
    displayFormat?.chars ?? NUMBER_SPIN_BUTTON_DEFAULTS.PADDING_CHARS;

  // Parse a given string into a number.
  const parse = useCallback((input: string) => {
    // Break early if input is empty
    if (input === '') {
      return null;
    }

    // Validate and extract the integer value from the given input
    const match = input.match(reParseValue);
    if (!match) {
      return null;
    }

    // parse matching integer
    const [value] = match;
    return parseInt(value);
  }, []);

  // Check if the given value is within given boundaries.
  const validate = useCallback(
    (value: number | null | undefined) => {
      if (required && value === null) {
        return false;
      }

      return (
        value === null ||
        value === undefined ||
        (!Number.isNaN(value) && value >= minValue && value <= maxValue)
      );
    },
    [maxValue, minValue, required],
  );

  // Formats the given input based on the configured display formatting.
  const format = useCallback(
    (input: string | number | null) => {
      // A null value is always shown as empty string. (e.g. to show the placeholder if configured)
      if (input === null || input === '') {
        return '';
      }

      // If input is a number, convert to string. Fallback to an empty string if input is invalid.
      const text =
        typeof input === 'string'
          ? input
          : isNil(input) || Number.isNaN(input)
          ? ''
          : input.toString();

      // If no formatting is configured, return the text.
      if (formatLength === 0) {
        return text;
      }

      return text.padStart(formatLength, formatChars);
    },
    [formatLength, formatChars],
  );

  return { validate, parse, format };
};
